import React, { useEffect, useRef } from 'react';
import { Icon } from '@iconify/react';
import SearchableSelect from '../SearchableSelect';
import KkpClientAutocomplete from './KkpClientAutocomplete';
import {
  createClientDraftFromInput,
  createClientDraftFromSelect,
  getClientDraftLabel,
} from '../../utils/kkpPlanningClientInline';

const BLUR_SAVE_DELAY_MS = 120;

const KkpPlanningMagInlineCell = ({
  venteId,
  field,
  editingCell,
  editDraft,
  onEditDraftChange,
  onStartEdit,
  onSave,
  saving,
  type = 'text',
  options = [],
  optionValueKey = 'value',
  optionLabelKey = 'label',
  allowEmpty = true,
  emptyLabel = '— Vide —',
  readOnly = false,
  isOptionBlocked = null,
  optionTitle = null,
  onBlockedOptionSelect = null,
  onSelectIntercept = null,
  inputMin = 0,
  inputMax,
  inputStep = 1,
  children,
  className = '',
}) => {
  const blurSaveTimerRef = useRef(null);
  const cellRef = useRef(null);
  const isEditing = editingCell?.venteId === venteId && editingCell?.field === field;

  const resolveOptionValue = (opt) => (typeof opt === 'object' ? opt[optionValueKey] : opt);

  const hasEmptyOption = options.some((opt) => {
    const val = resolveOptionValue(opt);
    return val === '' || val === null || val === undefined;
  });

  const showEmptyOption = allowEmpty && type === 'select' && !hasEmptyOption;

  const clearBlurSaveTimer = () => {
    if (blurSaveTimerRef.current) {
      window.clearTimeout(blurSaveTimerRef.current);
      blurSaveTimerRef.current = null;
    }
  };

  const scheduleBlurSave = () => {
    clearBlurSaveTimer();
    blurSaveTimerRef.current = window.setTimeout(() => {
      blurSaveTimerRef.current = null;
      onSave();
    }, BLUR_SAVE_DELAY_MS);
  };

  const handleSelectChange = (value) => {
    if (isOptionBlocked?.(value)) {
      onBlockedOptionSelect?.(value);
      return;
    }
    if (onSelectIntercept?.(value)) {
      onStartEdit(null);
      return;
    }
    onEditDraftChange(value);
    onSave(value);
  };

  const handleWrapBlur = (e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    scheduleBlurSave();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && type !== 'periode') {
      e.preventDefault();
      clearBlurSaveTimer();
      onSave();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      clearBlurSaveTimer();
      onStartEdit(null);
    }
  };

  useEffect(() => () => clearBlurSaveTimer(), []);

  useEffect(() => {
    if (!isEditing || type !== 'searchable-select') return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (cellRef.current?.contains(target)) return;
      if (target.closest('.searchable-select__menu')) return;
      clearBlurSaveTimer();
      onSave();
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isEditing, type, onSave]);

  if (isEditing) {
    return (
      <td ref={cellRef} className={`kkp-inline-edit-cell ${className}`}>
        <div
          className="kkp-inline-edit-wrap"
          onBlur={type === 'periode' ? handleWrapBlur : undefined}
        >
          {type === 'periode' && (
            <>
              <input
                type="number"
                className="form-control form-control-sm kkp-inline-edit-periode-annee"
                value={editDraft?.annee ?? ''}
                onChange={(e) => onEditDraftChange({ ...editDraft, annee: Number(e.target.value) })}
                onKeyDown={handleKeyDown}
                autoFocus
              />
              <select
                className="form-select form-select-sm"
                value={editDraft?.mois ?? ''}
                onChange={(e) => onEditDraftChange({ ...editDraft, mois: Number(e.target.value) })}
                onKeyDown={handleKeyDown}
              >
                {options.map((opt) => (
                  <option key={opt[optionValueKey]} value={opt[optionValueKey]}>
                    {opt[optionLabelKey]}
                  </option>
                ))}
              </select>
            </>
          )}
          {type === 'select' && (
            <select
              className="form-select form-select-sm"
              value={editDraft ?? ''}
              onChange={(e) => handleSelectChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={scheduleBlurSave}
              autoFocus
              disabled={saving}
            >
              {showEmptyOption && (
                <option value="">{emptyLabel}</option>
              )}
              {options.map((opt) => {
                const val = resolveOptionValue(opt);
                const label = typeof opt === 'object' ? opt[optionLabelKey] : opt;
                const blocked = Boolean(isOptionBlocked?.(val));
                return (
                  <option
                    key={String(val)}
                    value={val}
                    title={optionTitle?.(val)}
                    className={blocked ? 'kkp-statut-option--locked' : undefined}
                    style={blocked ? { color: '#9ca3af' } : undefined}
                  >
                    {label}
                  </option>
                );
              })}
            </select>
          )}
          {type === 'searchable-select' && (
            <SearchableSelect
              value={editDraft ?? ''}
              onChange={(value) => handleSelectChange(value)}
              options={options}
              allowEmpty={allowEmpty}
              emptyOptionLabel={emptyLabel}
              emptyLabel="Aucun résultat"
              disabled={saving}
              buttonClassName="form-select form-select-sm text-start"
            />
          )}
          {type === 'client' && (
            <KkpClientAutocomplete
              value={getClientDraftLabel(editDraft)}
              onChange={(val) => onEditDraftChange(createClientDraftFromInput(val))}
              onSelect={(client) => {
                clearBlurSaveTimer();
                const draft = createClientDraftFromSelect(client);
                onEditDraftChange(draft);
                onSave(draft);
              }}
              onKeyDown={handleKeyDown}
              onBlur={scheduleBlurSave}
              autoFocus
              className="kkp-inline-client-autocomplete position-relative flex-grow-1"
              inputClassName="form-control form-control-sm"
              size="sm"
            />
          )}
          {type === 'text' && (
            <input
              type="text"
              className="form-control form-control-sm"
              value={editDraft ?? ''}
              onChange={(e) => onEditDraftChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={scheduleBlurSave}
              autoFocus
            />
          )}
          {type === 'number' && (
            <input
              type="number"
              min={inputMin}
              {...(inputMax != null ? { max: inputMax } : {})}
              step={inputStep}
              className="form-control form-control-sm text-end"
              value={editDraft ?? ''}
              onChange={(e) => onEditDraftChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={scheduleBlurSave}
              autoFocus
              disabled={saving}
            />
          )}
        </div>
      </td>
    );
  }

  return (
    <td
      className={`kkp-inline-display-cell ${readOnly ? 'kkp-inline-display-cell--readonly' : ''} ${className}`}
      onDoubleClick={() => {
        if (!readOnly) onStartEdit(venteId, field);
      }}
      title={readOnly ? undefined : 'Double-cliquer ou crayon pour modifier — Échap pour annuler'}
    >
      <div className="kkp-inline-display-inner">
        <div className="kkp-inline-display-content">{children}</div>
        {!readOnly && (
          <button
            type="button"
            className="kkp-inline-edit-affordance"
            title="Modifier"
            aria-label="Modifier"
            onClick={(e) => {
              e.stopPropagation();
              onStartEdit(venteId, field);
            }}
          >
            <Icon icon="mdi:pencil-outline" width={14} height={14} />
          </button>
        )}
      </div>
    </td>
  );
};

export default KkpPlanningMagInlineCell;
